/**
 * Accelaser performance layer.
 *
 * A single place that owns everything that keeps the page cheap at runtime:
 * scroll observation, idle scheduling, page-visibility handling, carousel
 * lifecycle, ScrollTrigger refresh batching and on-demand script loading.
 *
 * Sections should express *what they need* ("tell me when this is near the
 * viewport") rather than each building their own observer, timer or loader.
 * Nothing here changes what a visitor sees - it only changes when work happens.
 *
 * Exposed as `window.theme.perf`. Safe to load before main.js: `theme` is
 * merged into, never replaced.
 */
(function () {
  'use strict';

  const theme = (window.theme = window.theme || {});

  // ---------------------------------------------------------------------------
  // IntersectionObserver pool
  // ---------------------------------------------------------------------------

  /**
   * Observers are grouped by their options. Ten sections asking to be told when
   * they are 500px away share one observer instead of creating ten.
   */
  const pools = [];

  /**
   * @param {string} rootMargin
   * @param {number|number[]} threshold
   * @returns {Object} Pool for that option combination.
   */
  function getPool(rootMargin, threshold) {
    for (let i = 0; i < pools.length; i++) {
      if (pools[i].rootMargin === rootMargin && pools[i].threshold === threshold) return pools[i];
    }

    const pool = {
      rootMargin: rootMargin,
      threshold: threshold,
      callbacks: new Map(),
      observer: null,
    };

    pool.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const callback = pool.callbacks.get(entry.target);
          pool.callbacks.delete(entry.target);
          pool.observer.unobserve(entry.target);

          if (typeof callback === 'function') callback(entry);
        });
      },
      { rootMargin: rootMargin, threshold: threshold }
    );

    pools.push(pool);
    return pool;
  }

  /**
   * Calls `callback` the first time `element` comes within `rootMargin` of the viewport.
   * @param {Element} element - Element to observe.
   * @param {Function} callback - Called once, on first intersection.
   * @param {Object} [options] - `{ rootMargin = '0px', threshold = 0 }`
   * @returns {Function} Unobserve function.
   */
  function observe(element, callback, options = {}) {
    if (!element) return () => {};

    const rootMargin = options.rootMargin || '0px';
    const threshold = options.threshold || 0;

    if (!('IntersectionObserver' in window)) {
      if (typeof callback === 'function') callback();
      return () => {};
    }

    const pool = getPool(rootMargin, threshold);
    pool.callbacks.set(element, callback);
    pool.observer.observe(element);

    return () => {
      pool.callbacks.delete(element);
      pool.observer.unobserve(element);
    };
  }

  /**
   * Signature-compatible replacement for main.js `initLazyScript`.
   * Runs `callback` once, when the element is within `threshold` px below the fold.
   * @param {Element} element
   * @param {Function} callback
   * @param {number} [threshold=500]
   */
  function lazyObserve(element, callback, threshold = 500) {
    observe(element, callback, { rootMargin: `0px 0px ${threshold}px 0px` });
  }

  // ---------------------------------------------------------------------------
  // Idle scheduling
  // ---------------------------------------------------------------------------

  /**
   * Runs `fn` on the next idle period, but never later than `timeout` ms.
   * requestIdleCallback is still missing in Safari, hence the fallback.
   * @param {Function} fn
   * @param {number} [timeout=2000]
   */
  function whenIdle(fn, timeout = 2000) {
    if (typeof fn !== 'function') return;

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: timeout });
      return;
    }

    setTimeout(fn, 1);
  }

  // ---------------------------------------------------------------------------
  // Page visibility
  // ---------------------------------------------------------------------------

  const visibilityCallbacks = [];

  /**
   * @returns {boolean} True while the tab is in the background.
   */
  function isHidden() {
    return document.visibilityState === 'hidden' || document.hidden === true;
  }

  /**
   * Registers `fn` to be called on every visibility change.
   * @param {Function} fn - Receives one boolean argument: true when visible.
   * @param {boolean} [immediate=true] - Also call `fn` right away with the current state.
   */
  function onVisibility(fn, immediate = true) {
    if (typeof fn !== 'function') return;
    visibilityCallbacks.push(fn);
    if (immediate) fn(!isHidden());
  }

  function broadcastVisibility() {
    const visible = !isHidden();
    document.documentElement.classList.toggle('perf-hidden', !visible);

    visibilityCallbacks.forEach((fn) => {
      try {
        fn(visible);
      } catch (error) {
        // One broken subscriber must not stop the others.
      }
    });
  }

  /**
   * Pauses decorative bare <video> elements while the tab is hidden.
   * Videos inside <video-component> are owned by video.js and skipped here.
   * @param {boolean} visible
   */
  function toggleBackgroundVideos(visible) {
    document.querySelectorAll('video').forEach((video) => {
      if (video.closest('video-component')) return;

      if (!visible) {
        if (video.paused) return;
        video.dataset.perfWasPlaying = 'true';
        video.pause();
        return;
      }

      if (video.dataset.perfWasPlaying !== 'true') return;
      delete video.dataset.perfWasPlaying;

      const promise = video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    });
  }

  document.addEventListener('visibilitychange', broadcastVisibility);
  // No immediate call: on first load there is nothing paused to resume, and the
  // initial sweep over every <video> would be wasted work.
  onVisibility(toggleBackgroundVideos, false);
  document.documentElement.classList.toggle('perf-hidden', isHidden());

  // ---------------------------------------------------------------------------
  // Swiper registry
  // ---------------------------------------------------------------------------

  /** @type {Array<{swiper: Object, el: Element, inViewport: boolean}>} */
  const swipers = [];
  const swipersByElement = new Map();
  let swiperObserver = null;

  /**
   * @param {Object} entry
   */
  function syncSwiper(entry) {
    const autoplay = entry.swiper && entry.swiper.autoplay;
    if (!autoplay || typeof autoplay.start !== 'function') return;

    const shouldRun = entry.inViewport && !isHidden();

    try {
      if (shouldRun && !autoplay.running) autoplay.start();
      else if (!shouldRun && autoplay.running) autoplay.stop();
    } catch (error) {
      // Swiper can throw while it is being destroyed mid-transition.
    }
  }

  /**
   * Registers a Swiper instance so its autoplay stops while it is off-screen or
   * while the tab is in the background, and resumes when it is watchable again.
   * @param {Object} instance - The value returned by `new Swiper(...)`.
   * @param {Element} [element] - Observed element, defaults to the Swiper root.
   * @returns {Object} The instance, so calls can wrap `new Swiper(...)` inline.
   */
  function registerSwiper(instance, element) {
    if (!instance) return instance;

    const el = element || instance.el;
    const entry = { swiper: instance, el: el, inViewport: true };
    swipers.push(entry);

    if (el && 'IntersectionObserver' in window) {
      if (!swiperObserver) {
        swiperObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((observerEntry) => {
              const tracked = swipersByElement.get(observerEntry.target);
              if (!tracked) return;
              tracked.inViewport = observerEntry.isIntersecting;
              syncSwiper(tracked);
            });
          },
          { rootMargin: '200px 0px 200px 0px' }
        );
      }

      swipersByElement.set(el, entry);
      swiperObserver.observe(el);
    }
  }

  onVisibility(() => {
    swipers.forEach(syncSwiper);
  });

  // ---------------------------------------------------------------------------
  // ScrollTrigger refresh batching
  // ---------------------------------------------------------------------------

  let scrollTriggerFrame = null;

  /**
   * Coalesces every refresh request in the same frame into one.
   * `resize` + `load` + `fonts.ready` firing together previously meant four or
   * more full re-measures of the page within a few milliseconds.
   */
  function refreshScrollTriggers() {
    if (!window.ScrollTrigger || scrollTriggerFrame !== null) return;

    scrollTriggerFrame = requestAnimationFrame(() => {
      scrollTriggerFrame = null;
      try {
        window.ScrollTrigger.refresh();
      } catch (error) {
        // Never let an animation library failure break the page.
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Script loading
  // ---------------------------------------------------------------------------

  const scriptPromises = {};

  /**
   * Injects a script once per URL. Repeat calls resolve with the same promise,
   * so several sections on one page sharing a library load it a single time.
   * @param {string} src - Absolute script URL.
   * @returns {Promise}
   */
  function loadScript(src) {
    if (!src) return Promise.reject(new Error('perf.loadScript: src is required'));
    if (scriptPromises[src]) return scriptPromises[src];

    scriptPromises[src] = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing && existing.dataset.perfLoaded === 'true') {
        resolve();
        return;
      }

      const script = existing || document.createElement('script');
      script.src = src;
      script.async = true;
      script.addEventListener('load', () => {
        script.dataset.perfLoaded = 'true';
        resolve();
      });
      script.addEventListener('error', () => {
        delete scriptPromises[src];
        reject(new Error(`perf.loadScript: failed to load ${src}`));
      });

      if (!existing) document.head.appendChild(script);
    });

    return scriptPromises[src];
  }

  /**
   * Resolves once GSAP and ScrollTrigger are both available. They are currently
   * in the document head, so this usually resolves immediately; it lets sections
   * move to on-demand loading later without changing their call sites.
   * @returns {Promise}
   */
  function requireGsap() {
    if (window.gsap && window.ScrollTrigger) return Promise.resolve();

    const assets = theme.perfAssets || {};
    if (!assets.gsap || !assets.scrollTrigger) {
      return Promise.reject(new Error('perf.requireGsap: theme.perfAssets is not configured'));
    }

    return loadScript(assets.gsap)
      .then(() => (window.ScrollTrigger ? undefined : loadScript(assets.scrollTrigger)))
      .then(() => undefined);
  }

  /**
   * Runs `callback` once the DOM is parsed *and* `src` has executed.
   *
   * Sections used to inline `document.addEventListener('DOMContentLoaded', ...)`
   * next to a globally loaded library. Swapping the opener for this keeps the
   * body untouched while the library only costs pages that actually use it.
   *
   * @param {string} src - Script URL, or a falsy value to just wait for the DOM.
   * @param {Function} callback
   */
  function whenScript(src, callback) {
    if (typeof callback !== 'function') return;

    const run = () => {
      if (!src) {
        callback();
        return;
      }

      loadScript(src).then(callback, (error) => {
        // The library could not be fetched. Initialising without it would throw
        // inside the callback, so report and leave the section in its static,
        // pre-enhancement state instead.
        if (window.console && console.warn) console.warn(error.message);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  }

  /**
   * `whenScript` bound to the Swiper library, whose URL comes from perfAssets.
   * @param {Function} callback
   */
  function whenSwiper(callback) {
    whenScript((theme.perfAssets || {}).swiper, callback);
  }

  // ---------------------------------------------------------------------------
  // Misc
  // ---------------------------------------------------------------------------

  /**
   * @returns {boolean} True when the visitor asked the OS for reduced motion.
   */
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  theme.perf = {
    observe: observe,
    lazyObserve: lazyObserve,
    whenIdle: whenIdle,
    isHidden: isHidden,
    onVisibility: onVisibility,
    registerSwiper: registerSwiper,
    refreshScrollTriggers: refreshScrollTriggers,
    loadScript: loadScript,
    requireGsap: requireGsap,
    whenScript: whenScript,
    whenSwiper: whenSwiper,
    prefersReducedMotion: prefersReducedMotion,
  };
})();
